import React from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';

type Props = {
  open: boolean;
  initialValue?: any | null;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => void;
};

const HEALTH_STATUSES = ['unknown', 'healthy', 'degraded', 'unhealthy'];

const emptyForm = () => ({
  slug: '',
  name: '',
  base_url: '',
  is_external: false,
  auth_config_ref: '',
  health_status: 'unknown',
  is_active: true,
});

const AgentA2AEndpointEditorDialog: React.FC<Props> = ({
                                                         open,
                                                         initialValue,
                                                         submitting,
                                                         onClose,
                                                         onSubmit,
                                                       }) => {
  const [form, setForm] = React.useState<any>(emptyForm());

  const isEdit = Boolean(form.id);

  React.useEffect(() => {
    if (!open) return;
    setForm({ ...emptyForm(), ...(initialValue || {}) });
  }, [initialValue, open]);

  const setField = (field: string, value: any) =>
    setForm((prev: any) => ({ ...prev, [field]: value }));

  const handleSubmit = () => {
    if (!String(form.slug || '').trim()) {
      window.alert('Slug is required.');
      return;
    }
    if (!String(form.name || '').trim()) {
      window.alert('Name is required.');
      return;
    }
    if (!String(form.base_url || '').trim()) {
      window.alert('Base URL is required.');
      return;
    }

    onSubmit({
      ...form,
      slug: String(form.slug).trim(),
      name: String(form.name).trim(),
      base_url: String(form.base_url).trim(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit A2A Endpoint' : 'Add A2A Endpoint'}</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              size="small"
              label="Slug"
              value={form.slug}
              onChange={(e) => setField('slug', e.target.value)}
              inputProps={{ maxLength: 64 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              size="small"
              label="Name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              inputProps={{ maxLength: 128 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              size="small"
              label="Base URL"
              value={form.base_url}
              onChange={(e) => setField('base_url', e.target.value)}
              inputProps={{ maxLength: 255 }}
              helperText="Root URL of the remote agent (Agent Card is resolved from here)"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Auth Config Ref"
              value={form.auth_config_ref || ''}
              onChange={(e) => setField('auth_config_ref', e.target.value)}
              inputProps={{ maxLength: 255 }}
              helperText="Reference to the stored credential (e.g. secret name)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              size="small"
              label="Health Status"
              value={form.health_status || 'unknown'}
              onChange={(e) => setField('health_status', e.target.value)}
            >
              {HEALTH_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6} display="flex" alignItems="center" gap={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!form.is_external}
                  onChange={(e) => setField('is_external', e.target.checked)}
                />
              }
              label="External"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!form.is_active}
                  onChange={(e) => setField('is_active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={!!submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!!submitting}>
          {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Endpoint'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentA2AEndpointEditorDialog;
